// src/features/services/sales/hooks/useSkuCatalog.ts
// ─────────────────────────────────────────────────────────────────────────────
// SKU Catalog — FE hook (hook-owns-all-state pattern).
//
// Owns: fetch, cache, mutations, derived state, error handling.
// Components are pure renderers.
// ─────────────────────────────────────────────────────────────────────────────
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSkuListApi,
  fetchSkuApi,
  fetchSkuDefaultsApi,
  upsertSkuApi,
  deactivateSkuApi,
  reactivateSkuApi,
  SKU_KEYS,
  type SkuDefinition,
  type SkuDefaults,
  type SkuListResponse,
  type UpsertSkuPayload,
} from '../api/skuCatalogApi';

/**
 * Full SKU catalog hook — list + CRUD mutations.
 *
 * @param params - Optional filters: category, active, limit
 */
export function useSkuCatalog(params?: { category?: string; active?: string; limit?: number }) {
  const qc = useQueryClient();

  const list = useQuery<SkuListResponse, Error>({
    queryKey: SKU_KEYS.list(params),
    queryFn: () => fetchSkuListApi(params),
    staleTime: 30_000,
  });

  const upsert = useMutation<SkuDefinition, Error, UpsertSkuPayload>({
    mutationFn: (p) => upsertSkuApi(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: SKU_KEYS.all }),
  });

  const deactivate = useMutation<SkuDefinition, Error, string>({
    mutationFn: (sku) => deactivateSkuApi(sku),
    onSuccess: () => qc.invalidateQueries({ queryKey: SKU_KEYS.all }),
  });

  const reactivate = useMutation<SkuDefinition, Error, string>({
    mutationFn: (sku) => reactivateSkuApi(sku),
    onSuccess: () => qc.invalidateQueries({ queryKey: SKU_KEYS.all }),
  });

  return {
    ...list,
    upsert,
    deactivate,
    reactivate,
  };
}

/**
 * Single SKU detail hook — fetches a single SKU definition by code.
 *
 * @param sku - SKU code (scope_key)
 */
export function useSkuDetail(sku: string | null) {
  return useQuery<SkuDefinition, Error>({
    queryKey: SKU_KEYS.detail(sku!),
    queryFn: () => fetchSkuApi(sku!),
    enabled: !!sku,
    staleTime: 30_000,
  });
}

/**
 * SKU defaults hook — fetches IoT config template for a SKU.
 * Used by sales-order creation to pre-populate device defaults.
 *
 * @param sku - SKU code
 */
export function useSkuDefaults(sku: string | null) {
  return useQuery<SkuDefaults, Error>({
    queryKey: SKU_KEYS.defaults(sku!),
    queryFn: () => fetchSkuDefaultsApi(sku!),
    enabled: !!sku,
    staleTime: 60_000,
  });
}
